import React from 'react';
import { Bell } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import type { TimesheetDeadlineSettings } from '@/types/timesheetSettings';
import { getDayName } from '@/types/timesheetSettings';

interface TimesheetReminderSettingsProps {
  settings: TimesheetDeadlineSettings;
  onChange: (settings: TimesheetDeadlineSettings, isValid: boolean) => void;
}

// ---------------------------------------------------------------------------
// Setting card — matches General Settings / Delegation card pattern
// ---------------------------------------------------------------------------

interface SettingCardProps {
  id: string;
  label: string;
  description: string;
  control: React.ReactNode;
  onClick?: () => void;
}

function SettingCard({ id, label, description, control, onClick }: SettingCardProps) {
  return (
    <div
      className={`flex items-center justify-between gap-4 p-4 rounded-lg bg-background transition-colors
        ${onClick ? 'cursor-pointer select-none hover:bg-accent/50' : ''}
      `}
      onClick={onClick}
    >
      <div className="flex-1 min-w-0">
        <Label
          htmlFor={id}
          className={`text-sm font-medium leading-snug ${onClick ? 'cursor-pointer select-none' : ''}`}
        >
          {label}
        </Label>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
      </div>
      <div onClick={(e) => e.stopPropagation()} className="shrink-0">
        {control}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TimesheetReminderSettings: React.FC<TimesheetReminderSettingsProps> = ({
  settings,
  onChange,
}) => {
  const handleSendRemindersChange = (checked: boolean) => {
    onChange({ ...settings, sendReminders: checked }, true);
  };

  // Format deadline time for display (e.g., "17:00" -> "5:00 PM")
  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const deadlineDay = getDayName(settings.deadlineDay);
  const deadlineTime = formatTime(settings.deadlineTime);

  return (
    <Accordion type="multiple" defaultValue={['reminders']} className="space-y-4">
      <AccordionItem value="reminders" className="border rounded-lg">
        <AccordionTrigger className="px-4 hover:no-underline">
          <div className="flex items-center gap-3 text-left">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Bell className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="font-semibold">Submission Reminders</p>
              <p className="text-xs text-muted-foreground font-normal mt-0.5">
                Configure reminders for timesheets
              </p>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pt-2 pb-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <SettingCard
              id="sendReminders"
              label="Submission Deadline Reminders"
              description={`Enable feed notifications ${settings.reminderDaysBefore} ${settings.reminderDaysBefore === 1 ? 'day' : 'days'} before the ${deadlineDay} deadline at ${deadlineTime}`}
              control={
                <Checkbox
                  id="sendReminders"
                  checked={settings.sendReminders}
                  onCheckedChange={(v) => handleSendRemindersChange(!!v)}
                />
              }
              onClick={() => handleSendRemindersChange(!settings.sendReminders)}
            />

          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
